import { useCallback, useMemo, useState } from "react";
import { downloadFile, isPreviewable, type PartialFileUpload } from "../utils";
import type { FilePreviewDialogProps } from "./file-preview-dialog";

export interface UseFilePreviewDialogOptions<T extends PartialFileUpload> {
  files: T[];
  selectedFile?: T;
  setSelectedFile?: (file: T | undefined) => void;
}

export const useFilePreviewDialog = <T extends PartialFileUpload>({
  files,
  selectedFile: controlledSelectedFile,
  setSelectedFile: controlledSetSelectedFile,
}: UseFilePreviewDialogOptions<T>) => {
  const [uncontrolledSelectedFile, setUncontrolledSelectedFile] = useState<T>();
  const isControlled = controlledSetSelectedFile !== undefined;
  const selectedFile = isControlled ? controlledSelectedFile : uncontrolledSelectedFile;
  const previewableFiles = useMemo(() => files.filter((file) => isPreviewable(file.mimeType)), [files]);
  const dialogFiles = useMemo(() => {
    if (!selectedFile || previewableFiles.some((file) => file.id === selectedFile.id)) {
      return previewableFiles;
    }

    return [selectedFile];
  }, [previewableFiles, selectedFile]);

  const setSelectedFile = useCallback(
    (file: T | undefined) => {
      if (controlledSetSelectedFile) {
        controlledSetSelectedFile(file);
        return;
      }

      setUncontrolledSelectedFile(file);
    },
    [controlledSetSelectedFile]
  );

  const openFile = useCallback(
    (file: T) => {
      if (isPreviewable(file.mimeType)) {
        setSelectedFile(file);
        return;
      }

      void downloadFile(file);
    },
    [setSelectedFile]
  );

  const dialogProps = useMemo(
    (): FilePreviewDialogProps<T> => ({
      file: selectedFile,
      files: dialogFiles,
      setSelectedFile,
    }),
    [dialogFiles, selectedFile, setSelectedFile]
  );

  return {
    dialogProps,
    openFile,
    previewableFiles,
    selectedFile,
    setSelectedFile,
  };
};
